import { Injector, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';
import { awaitSessionRestore } from './await-session';

/**
 * Guard for company-scoped routes (`/:companySlug/...`): verifies the current
 * user actually belongs to `:companySlug` before letting the route activate.
 *
 * Without this, a stale bookmark/deep-link — or the URL the home guard picked
 * before a `loadCompanies()` response had loaded — could render a page for a
 * company the user has no access to instead of redirecting.
 *
 * Admins bypass the check, matching the backend's `assertCompanyAccess`.
 */
export const companyAccessGuard: CanActivateFn = async (route) => {
	const router = inject(Router);
	const auth = inject(AuthService);
	const companyService = inject(CompanyService);
	const injector = inject(Injector);

	const slug = route.paramMap.get('companySlug');
	if (!slug) return true;

	// Guards on one route run concurrently, so `authGuard` being listed first
	// guarantees nothing — without this wait, `user()` reads null and the
	// `loadCompanies()` below goes out unauthenticated.
	await awaitSessionRestore(auth, injector);

	// No session: let authGuard own the redirect rather than firing a request
	// that is certain to 401.
	if (!auth.isAuthenticated()) return false;

	if (auth.user()?.isAdmin) return true;

	if (companyService.companies().length === 0) {
		await firstValueFrom(companyService.loadCompanies());
	}

	const hasAccess = companyService.companies().some(c => c.slug === slug);
	if (!hasAccess) {
		router.navigate(['/']);
		return false;
	}
	return true;
};
